/**
 * Phase R — Per-section clone of `pages/WMS/WMSSettings.jsx` (FULL Salem parity).
 *
 * MACHINE-GENERATED by `scripts/build_per_section_wms_setup_frontend.py`.
 * Mounted at `/sections/dispatch/:section/wms-setup/<flow>`. All `${apiBase}/...`
 * API calls are rewritten to `/per-section/${slug}/wms-setup/...`.
 *
 * DO NOT hand-edit. Edit Salem's source of truth and re-run the transformer.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Settings, Clock, Bell, Save, CheckCircle, Search, Pencil, Check, X, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo',
  art_silk: 'Art Silk',
  kids: 'Kids',
};

// PHASE_R_FULL_CLONE = true (sentinel for tests)

const PerSectionWMSSettings = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug];
  const basePath = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/wms-setup`;
  const [settings, setSettings] = useState({
    auto_trigger_time: '06:00',
    auto_trigger_enabled: false,
    last_auto_run: null,
    last_manual_run: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Learned capacity state
  const [capacityRecords, setCapacityRecords] = useState([]);
  const [capacitySearch, setCapacitySearch] = useState('');
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchCapacity();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${apiBase}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCapacity = useCallback(async (search = '') => {
    try {
      setCapacityLoading(true);
      const response = await apiClient.get(`${apiBase}/settings/learned-capacity?search=${encodeURIComponent(search)}`);
      setCapacityRecords(response.data.records || []);
    } catch (error) {
      toast.error('Failed to fetch learned capacity');
    } finally {
      setCapacityLoading(false);
    }
  }, []);

  const handleCapacitySearch = (value) => {
    setCapacitySearch(value);
    if (value.length === 0 || value.length >= 2) {
      fetchCapacity(value);
    }
  };

  const startEdit = (record) => {
    setEditingRow(`${record.design_name}|${record.bin_type}`);
    setEditValue(String(record.learned_capacity || 0));
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditValue('');
  };

  const saveCapacity = async (record) => {
    try {
      await apiClient.put(`${apiBase}/settings/learned-capacity`, {
        design_name: record.design_name,
        bin_type: record.bin_type,
        learned_capacity: parseInt(editValue)
      });
      toast.success(`Capacity updated to ${editValue}`);
      setEditingRow(null);
      fetchCapacity(capacitySearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update capacity');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiClient.put(`${apiBase}/settings`, {
        auto_trigger_time: settings.auto_trigger_time,
        auto_trigger_enabled: settings.auto_trigger_enabled
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
  if (!sectionName) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFBF5] p-6" data-testid={`persectionwmssettings-unknown`}>
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-500 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
      </div>
    );
  }

    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center" data-testid={`persectionwmssettings-${slug}`}>
        <p className="text-[#5A5F6E]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate(basePath)}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6" />
            <div>
              <h1 className="text-xl font-bold font-display">WMS Settings</h1>
              <p className="text-sm text-[#E8DCC8]">Configure auto-replenishment</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Auto Trigger Settings */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#2D5F4F]" />
            Auto Replenishment Schedule
          </h2>

          <div className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Enable Auto Replenishment</Label>
                <p className="text-sm text-[#5A5F6E] mt-1">
                  Automatically check and generate pick slips daily
                </p>
              </div>
              <Switch
                checked={settings.auto_trigger_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({
                  ...prev,
                  auto_trigger_enabled: checked
                }))}
                data-testid="auto-trigger-toggle"
              />
            </div>

            {/* Trigger Time */}
            <div>
              <Label>Daily Trigger Time</Label>
              <p className="text-sm text-[#5A5F6E] mb-2">
                Time when the system will automatically check for replenishment needs
              </p>
              <Input
                type="time"
                value={settings.auto_trigger_time}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  auto_trigger_time: e.target.value
                }))}
                className="w-40"
                data-testid="trigger-time-input"
              />
            </div>

            {/* Last Run Info */}
            <div className="pt-4 border-t border-[#E5DFD0]">
              <h3 className="text-sm font-medium text-[#1A1F2E] mb-3">Last Run Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-[#5A5F6E]">Last Auto Run</p>
                  <p className="font-medium text-[#1A1F2E]">
                    {settings.last_auto_run 
                      ? new Date(settings.last_auto_run).toLocaleString()
                      : 'Never'
                    }
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-[#5A5F6E]">Last Manual Run</p>
                  <p className="font-medium text-[#1A1F2E]">
                    {settings.last_manual_run 
                      ? new Date(settings.last_manual_run).toLocaleString()
                      : 'Never'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications Settings */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#2D5F4F]" />
            Notifications
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">In-App Notifications</Label>
                <p className="text-sm text-[#5A5F6E]">
                  Receive notifications when pick slips are generated
                </p>
              </div>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Partial Slip Alerts</Label>
                <p className="text-sm text-[#5A5F6E]">
                  Alert when SFG stock is insufficient for full replenishment
                </p>
              </div>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>

        {/* Replenishment Logic Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="font-medium text-blue-900 mb-3">Replenishment Logic</h3>
          <div className="space-y-2 text-sm text-blue-800">
            <p><strong>Trigger Condition:</strong> FG Stock ≤ (Next Month Projection - Min Case Pack Size)</p>
            <p><strong>Pick Quantity:</strong> Next Month Projection - Current FG Stock (rounded up to full bundles)</p>
            <p><strong>Bundle Selection:</strong> FIFO (First In, First Out)</p>
            <p><strong>Partial Slips:</strong> Generated when SFG stock is insufficient</p>
          </div>
        </div>

        {/* Learned Bin Capacity */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-[#2D5F4F]" />
            Learned Bin Capacity
          </h2>
          <p className="text-sm text-[#5A5F6E] mb-4">
            Capacities learned when staff marks a bin as full. Edit values if incorrect.
          </p>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              placeholder="Filter by design name or barcode..."
              value={capacitySearch}
              onChange={(e) => handleCapacitySearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="capacity-search"
            />
          </div>

          {/* Table */}
          {capacityLoading ? (
            <p className="text-center py-4 text-gray-500">Loading...</p>
          ) : capacityRecords.length === 0 ? (
            <p className="text-center py-4 text-gray-500">No records found</p>
          ) : (
            <div className="overflow-auto max-h-[500px] border border-gray-200 rounded-lg">
              <table className="w-full text-sm" data-testid="capacity-table">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Design Name</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Bin Type</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Capacity</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Last Recorded</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600 w-24">Edit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {capacityRecords.map((rec) => {
                    const rowKey = `${rec.design_name}|${rec.bin_type}`;
                    const isEditing = editingRow === rowKey;
                    return (
                      <tr key={rowKey} className="hover:bg-gray-50" data-testid={`capacity-row-${rowKey}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{rec.design_name}</div>
                          {rec.shade && <span className="text-xs text-gray-500">{rec.shade} / {rec.size}</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{rec.bin_type}</td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <Input
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="w-24 h-8 text-sm"
                              autoFocus
                              min={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveCapacity(rec);
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              data-testid="capacity-edit-input"
                            />
                          ) : (
                            <span className="font-semibold text-gray-900" data-testid="capacity-value">{rec.learned_capacity}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {rec.last_recorded_at ? new Date(rec.last_recorded_at).toLocaleDateString('en-IN') : '-'}
                          {rec.last_bin_code && <div className="text-gray-400">{rec.last_bin_code}</div>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-1">
                              <Button size="sm" variant="ghost" onClick={() => saveCapacity(rec)} className="h-7 w-7 p-0 text-green-600 hover:bg-green-50" data-testid="capacity-save-btn">
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid="capacity-cancel-btn">
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => startEdit(rec)} className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50" data-testid="capacity-edit-btn">
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2">{capacityRecords.length} records</p>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-[#2D5F4F] hover:bg-[#234A3D] text-white h-12"
          data-testid="save-settings-btn"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </main>
    </div>
  );
};

export default PerSectionWMSSettings;
